import { TFunction } from 'react-i18next';
import { Payment, PaymentStatus } from '../types/payment';
import { Document } from '../types/document';
import { ChangeRequest, ChangeRequestStatus } from '../types/changeRequest';
import {
  InstitutionDto,
  InstitutionMembershipCategory,
  getInstitutionDisplayName,
} from '../types/institution';
import { User } from '../types/auth';
import { BilingualName } from '../types/common';
import {
  InstitutionDashboardViewModel,
  DashboardActivityItem,
  DashboardUrgentNotice,
  InstitutionProfileFieldKey,
  InstitutionPersonView,
} from '../types/dashboard';
import { formatCurrency, formatDate, formatActivityTimestamp } from './dashboardViewModel';
import { tryNormalizeEnumValue } from './enumNormalize';

export const INSTITUTION_PROFILE_FIELD_LABEL_KEYS: Record<InstitutionProfileFieldKey, string> = {
  institutionName: 'register.institutionName',
  primaryAddress: 'register.institutionAddress',
  institutionEmail: 'register.institutionEmail',
  institutionPhone: 'register.institutionTel',
  directorName: 'register.institutionDirector',
  directorEmail: 'register.directorEmail',
  icomRepresentativeName: 'register.icomRepresentative',
  icomRepresentativeEmail: 'register.icomRepresentativeEmail',
  website: 'register.website',
  membershipCategory: 'register.membershipCategory',
  administrativeStatus: 'register.administrativeStatus',
};

/** Extra field → i18n key map for change-request / display labels beyond completeness fields. */
const INSTITUTION_FIELD_LABEL_KEYS_EXTRA: Record<string, string> = {
  foundationDate: 'register.foundationDate',
  secondaryAddress: 'register.secondaryAddress',
  contactPreference: 'register.contactPreference',
  icomContactPreference: 'register.contactPreference',
  numberOfMemberCards: 'register.numberOfMemberCards',
  registrationYear: 'profile.registrationYear',
  directorPhone: 'register.directorPhone',
  icomRepresentativePhone: 'register.icomRepresentativePhone',
  icomRepresentativePosition: 'register.icomRepresentativePosition',
  icomRepresentativeCommunicationLanguage: 'register.languageForCommunication',
  contactPersonFirstName: 'register.contactPersonFirstName',
  contactPersonLastName: 'register.contactPersonLastName',
  contactPersonEmail: 'register.contactPersonEmail',
  contactPersonPhone: 'register.contactPersonPhone',
  contactPerson: 'register.contactPerson',
  institutionType: 'profile.institutionType',
  collectionTypes: 'profile.collectionTypes',
};

export function getInstitutionFieldLabel(fieldName: string, t: TFunction): string {
  const normalized = fieldName.trim();
  if (!normalized) return normalized;

  const fromRequired =
    INSTITUTION_PROFILE_FIELD_LABEL_KEYS[normalized as InstitutionProfileFieldKey];
  if (fromRequired) return t(fromRequired);

  const fromExtra = INSTITUTION_FIELD_LABEL_KEYS_EXTRA[normalized];
  if (fromExtra) return t(fromExtra);

  const matchRequired = Object.entries(INSTITUTION_PROFILE_FIELD_LABEL_KEYS).find(
    ([key]) => key.toLowerCase() === normalized.toLowerCase()
  );
  if (matchRequired) return t(matchRequired[1]);

  const matchExtra = Object.entries(INSTITUTION_FIELD_LABEL_KEYS_EXTRA).find(
    ([key]) => key.toLowerCase() === normalized.toLowerCase()
  );
  if (matchExtra) return t(matchExtra[1]);

  return normalized;
}

const REQUIRED_INSTITUTION_FIELDS: InstitutionProfileFieldKey[] = [
  'institutionName',
  'primaryAddress',
  'institutionEmail',
  'institutionPhone',
  'directorName',
  'directorEmail',
  'icomRepresentativeName',
  'icomRepresentativeEmail',
  'website',
  'membershipCategory',
  'administrativeStatus',
];

interface CommitteeWithRepresentative {
  id: string;
  voteNumber?: number;
  name?: BilingualName;
  representativeEmail?: string;
  representativePhone?: string;
  representativePosition?: string;
  committee?: { nameEn?: string; nameGr?: string };
}

function getBilingualName(name: BilingualName | undefined, language: string): string {
  if (!name) return '';
  const isGr = language === 'gr' || language.startsWith('gr');
  const first = isGr
    ? name.firstNameGr || name.firstNameEn || ''
    : name.firstNameEn || name.firstNameGr || '';
  const last = isGr
    ? name.lastNameGr || name.lastNameEn || ''
    : name.lastNameEn || name.lastNameGr || '';
  return `${first} ${last}`.trim();
}

function truncateFilename(name: string, max = 40): string {
  if (name.length <= max) return name;
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const base = name.slice(0, max - ext.length - 1);
  return `${base}…${ext}`;
}

export function getMembershipCategoryLabel(
  category: InstitutionMembershipCategory | string | number | null | undefined,
  t: TFunction
): string {
  const labels: Record<InstitutionMembershipCategory, string> = {
    [InstitutionMembershipCategory.ActiveI]: t('register.budgetLessThan30k'),
    [InstitutionMembershipCategory.ActiveII]: t('register.budget30kTo100k'),
    [InstitutionMembershipCategory.ActiveIII]: t('register.budget100kTo1M'),
    [InstitutionMembershipCategory.ActiveIV]: t('register.budget1MTo5M'),
    [InstitutionMembershipCategory.ActiveV]: t('register.budget5MTo10M'),
    [InstitutionMembershipCategory.ActiveVI]: t('register.budgetMoreThan10M'),
    [InstitutionMembershipCategory.Benefactor]: t('register.budgetBenefactor'),
  };
  const normalized = tryNormalizeEnumValue(InstitutionMembershipCategory, category);
  if (normalized == null) return t('common.unknown', { defaultValue: 'Unknown' });
  return labels[normalized] ?? t('common.unknown', { defaultValue: 'Unknown' });
}

export function calculateInstitutionProfileCompletion(
  institution: InstitutionDto | null,
  t: TFunction
): {
  percentage: number;
  missingFieldKeys: InstitutionProfileFieldKey[];
  missingFieldLabels: string[];
  completedFieldKeys: InstitutionProfileFieldKey[];
  completedFieldLabels: string[];
} {
  if (!institution) {
    return {
      percentage: 0,
      missingFieldKeys: [...REQUIRED_INSTITUTION_FIELDS],
      missingFieldLabels: REQUIRED_INSTITUTION_FIELDS.map(k => t(INSTITUTION_PROFILE_FIELD_LABEL_KEYS[k])),
      completedFieldKeys: [],
      completedFieldLabels: [],
    };
  }

  const institutionName =
    institution.institutionName?.en ||
    institution.institutionName?.gr ||
    institution.institutionNameDisplay ||
    '';
  const primaryAddress =
    institution.primaryAddress?.addressEn ||
    institution.primaryAddress?.addressGr ||
    '';
  const directorName = getBilingualName(institution.directorName, 'en');
  const icomRepName = getBilingualName(institution.icomRepresentativeName, 'en');

  const fieldValues: Record<InstitutionProfileFieldKey, string> = {
    institutionName,
    primaryAddress,
    institutionEmail: institution.institutionEmail ?? '',
    institutionPhone: institution.institutionPhone ?? '',
    directorName,
    directorEmail: institution.directorEmail ?? '',
    icomRepresentativeName: icomRepName,
    icomRepresentativeEmail: institution.icomRepresentativeEmail ?? '',
    website: institution.website ?? '',
    membershipCategory:
      institution.membershipCategory != null ? String(institution.membershipCategory) : '',
    administrativeStatus:
      institution.administrativeStatus != null ? String(institution.administrativeStatus) : '',
  };

  const completedFieldKeys: InstitutionProfileFieldKey[] = [];
  const missingFieldKeys: InstitutionProfileFieldKey[] = [];

  REQUIRED_INSTITUTION_FIELDS.forEach(field => {
    const value = fieldValues[field];
    if (value !== null && value !== undefined && value !== '') {
      completedFieldKeys.push(field);
    } else {
      missingFieldKeys.push(field);
    }
  });

  const percentage = Math.round(
    (completedFieldKeys.length / REQUIRED_INSTITUTION_FIELDS.length) * 100
  );

  return {
    percentage,
    missingFieldKeys,
    missingFieldLabels: missingFieldKeys.map(k => t(INSTITUTION_PROFILE_FIELD_LABEL_KEYS[k])),
    completedFieldKeys,
    completedFieldLabels: completedFieldKeys.map(k => t(INSTITUTION_PROFILE_FIELD_LABEL_KEYS[k])),
  };
}

function buildInstitutionPeople(
  institution: InstitutionDto | null,
  language: string
): InstitutionPersonView[] {
  if (!institution) return [];

  const people: InstitutionPersonView[] = [];
  const isGr = language === 'gr' || language.startsWith('gr');

  const directorName = getBilingualName(institution.directorName, language);
  if (directorName || institution.directorEmail) {
    people.push({
      id: 'director',
      roleLabelKey: 'register.institutionDirector',
      displayName: directorName || institution.directorEmail || '',
      email: institution.directorEmail,
      phone: institution.directorPhone,
    });
  }

  const icomRepName = getBilingualName(institution.icomRepresentativeName, language);
  if (icomRepName || institution.icomRepresentativeEmail) {
    people.push({
      id: 'icom-representative',
      roleLabelKey: 'register.icomRepresentative',
      displayName: icomRepName || institution.icomRepresentativeEmail || '',
      email: institution.icomRepresentativeEmail,
      phone: institution.icomRepresentativePhone,
      position: institution.icomRepresentativePosition,
    });
  }

  const repRoleKeys = [
    'register.representative1',
    'register.representative2',
    'register.representative3',
  ] as const;

  const committees = (institution.committees ?? []) as CommitteeWithRepresentative[];
  committees
    .filter(c => c.name || c.representativeEmail)
    .sort((a, b) => (a.voteNumber ?? 99) - (b.voteNumber ?? 99))
    .forEach((committee, index) => {
      const repName = getBilingualName(committee.name, language);
      const committeeName = committee.committee
        ? isGr
          ? committee.committee.nameGr || committee.committee.nameEn
          : committee.committee.nameEn || committee.committee.nameGr
        : undefined;
      const roleIndex = Math.min(
        Math.max((committee.voteNumber ?? index + 1) - 1, 0),
        repRoleKeys.length - 1
      );

      people.push({
        id: committee.id,
        roleLabelKey: repRoleKeys[roleIndex],
        displayName: repName || committee.representativeEmail || '',
        email: committee.representativeEmail,
        phone: committee.representativePhone,
        position: committee.representativePosition,
        committeeName,
      });
    });

  return people;
}

function generateInstitutionActivities(
  payments: Payment[],
  documents: Document[],
  changeRequests: ChangeRequest[],
  t: TFunction,
  locale: string
): DashboardActivityItem[] {
  const activities: DashboardActivityItem[] = [];

  payments.slice(0, 3).forEach(payment => {
    const isPaid = payment.status === PaymentStatus.Paid;
    activities.push({
      id: `payment-${payment.id}`,
      type: 'payment',
      title: isPaid ? t('dashboard.activity.paymentPaid') : t('dashboard.activity.paymentPending'),
      description: formatCurrency(payment.amount, locale),
      timestamp: payment.paidAt || payment.createdAt,
      formattedTimestamp: formatActivityTimestamp(payment.paidAt || payment.createdAt, locale),
      link: '/payments',
      statusLabel: isPaid ? t('dashboard.subscription.paid') : t('dashboard.subscription.pending'),
      statusVariant: isPaid ? 'success' : 'warning',
    });
  });

  documents.slice(0, 3).forEach(doc => {
    activities.push({
      id: `document-${doc.id}`,
      type: 'document',
      title: doc.isVerified
        ? t('dashboard.activity.documentVerified')
        : t('dashboard.activity.documentUploaded'),
      description: t('dashboard.activity.documentDescription', {
        name: truncateFilename(doc.originalFileName),
      }),
      fullDescription: doc.originalFileName,
      timestamp: doc.uploadedAt,
      formattedTimestamp: formatActivityTimestamp(doc.uploadedAt, locale),
      link: '/documents',
      statusLabel: doc.isVerified ? t('dashboard.verified') : t('dashboard.pending'),
      statusVariant: doc.isVerified ? 'success' : 'warning',
    });
  });

  changeRequests.slice(0, 3).forEach(request => {
    let title: string;
    let variant: 'success' | 'warning' | 'error' = 'warning';
    if (request.status === ChangeRequestStatus.Approved) {
      title = t('dashboard.activity.changeRequestApproved');
      variant = 'success';
    } else if (request.status === ChangeRequestStatus.Rejected) {
      title = t('dashboard.activity.changeRequestRejected');
      variant = 'error';
    } else {
      title = t('dashboard.activity.changeRequestPending');
    }
    activities.push({
      id: `change-${request.id}`,
      type: 'changeRequest',
      title,
      description: t('changeRequests.institution'),
      timestamp: request.requestedAt,
      formattedTimestamp: formatActivityTimestamp(request.requestedAt, locale),
      link: '/change-requests',
      statusVariant: variant,
    });
  });

  return activities
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);
}

export interface InstitutionDashboardRawData {
  user: User;
  institution: InstitutionDto | null;
  payments: Payment[];
  documents: Document[];
  changeRequests: ChangeRequest[];
  language: string;
}

export function buildInstitutionDashboardViewModel(
  data: InstitutionDashboardRawData,
  t: TFunction
): InstitutionDashboardViewModel {
  const { user, institution, payments, documents, changeRequests, language } = data;
  const locale = language;
  const currentYear = new Date().getFullYear();
  const currentPayment = payments.find(p => p.year === currentYear) ?? null;

  const institutionName =
    (institution ? getInstitutionDisplayName(institution) : '') ||
    user.museumName ||
    user.email ||
    '';

  const openChangeRequests = changeRequests.filter(
    cr => cr.status === ChangeRequestStatus.Pending
  ).length;
  const verifiedDocuments = documents.filter(d => d.isVerified).length;
  const unverifiedDocuments = documents.filter(d => !d.isVerified).length;
  const profile = calculateInstitutionProfileCompletion(institution, t);
  const committeesCount = institution?.committees?.length ?? 0;
  const memberCardsCount = institution?.numberOfMemberCards ?? 0;

  const urgentNotices: DashboardUrgentNotice[] = [];

  if (currentPayment && currentPayment.status === PaymentStatus.Pending) {
    urgentNotices.push({
      type: 'warning',
      title: t('dashboard.pendingPayment'),
      message: t('dashboard.pendingPaymentMessage', {
        amount: formatCurrency(currentPayment.amount, locale),
        year: currentPayment.year,
      }),
      actionLabel: t('dashboard.subscription.payNow'),
      actionLink: '/payments',
    });
  }

  if (user && 'emailConfirmed' in user && !user.emailConfirmed) {
    urgentNotices.push({
      type: 'warning',
      title: t('dashboard.emailNotConfirmed'),
      message: t('dashboard.emailNotConfirmedMessage'),
    });
  }

  const membershipCategoryLabel = institution
    ? getMembershipCategoryLabel(institution.membershipCategory, t)
    : '';

  return {
    greeting: institutionName
      ? t('dashboard.institution.greeting', { name: institutionName })
      : t('dashboard.greetingFallback'),
    portalDescription: t('dashboard.institution.portalDescription'),
    subscription: {
      year: currentYear,
      payment: currentPayment,
      isPaid: currentPayment?.status === PaymentStatus.Paid,
      isPending: currentPayment?.status === PaymentStatus.Pending,
      formattedAmount: currentPayment ? formatCurrency(currentPayment.amount, locale) : null,
      paidOnLabel: currentPayment?.paidAt
        ? t('dashboard.subscription.paidOn', {
            date: formatDate(currentPayment.paidAt, locale),
          })
        : null,
      dueDateLabel:
        currentPayment && currentPayment.status === PaymentStatus.Pending
          ? t('dashboard.subscription.dueBy', {
              date: formatDate(new Date(currentPayment.year, 11, 31).toISOString(), locale),
            })
          : null,
      statusLabel: currentPayment
        ? currentPayment.status === PaymentStatus.Paid
          ? t('dashboard.subscription.paid')
          : t('dashboard.subscription.pending')
        : null,
      hasReceipt: currentPayment?.hasReceipt ?? false,
      showPayNow: currentPayment?.status === PaymentStatus.Pending,
    },
    identity: {
      institutionName,
      email: institution?.institutionEmail ?? user.email ?? '',
      membershipBadge: t('dashboard.roles.institutionMember'),
      membershipCategoryLabel,
      memberCardsAllocated: memberCardsCount,
      avatarName: institutionName || '?',
    },
    overview: {
      documentsCount: documents.length,
      documentsSupporting: t('dashboard.overview.documentsSupporting', {
        verified: verifiedDocuments,
        pending: unverifiedDocuments,
      }),
      openRequestsCount: openChangeRequests,
      totalRequestsCount: changeRequests.length,
      totalRequestsLabel:
        changeRequests.length > 0
          ? t('dashboard.overview.totalRequests', { count: changeRequests.length })
          : null,
      profilePercentage: profile.percentage,
      missingFieldsCount: profile.missingFieldKeys.length,
      missingFieldsSupporting:
        profile.missingFieldKeys.length > 0
          ? t('dashboard.overview.missingFieldsCount', {
              count: profile.missingFieldKeys.length,
            })
          : t('dashboard.complete'),
      memberCardsCount,
      memberCardsSupporting: t('dashboard.institution.overview.memberCardsSupporting'),
      committeesCount,
      committeesSupporting:
        committeesCount > 0
          ? t('dashboard.institution.overview.committeesSupporting', { count: committeesCount })
          : t('dashboard.institution.overview.noCommittees'),
    },
    profileCompletion: {
      percentage: profile.percentage,
      missingFieldLabels: profile.missingFieldLabels,
      completedFieldLabels: profile.completedFieldLabels,
      remainingMessage: t('dashboard.profileCompletion.remaining', {
        count: profile.missingFieldKeys.length,
      }),
    },
    activities: generateInstitutionActivities(
      payments,
      documents,
      changeRequests,
      t,
      locale
    ),
    urgentNotices,
    people: buildInstitutionPeople(institution, language),
  };
}
